import { useSetAtom } from "jotai";
import { type FC, type RefObject } from "react";

import {
  Box,
  FeaturedImageModule,
  Grid,
  NavMenuDrawer,
  NavProductList,
  NavShopAllLink,
  Paragraph,
  activeNavDropdownAtom,
} from "@theme/components";

import type { NavCategory } from "@theme/types";

const TOTAL_NUMBER_OF_COLUMNS = 4;

export interface NavDesktopCategoryDropdownProps {
  navCategory: NavCategory;
  buttonRef: RefObject<HTMLButtonElement>;
}

export const NavDesktopCategoryDropdown: FC<NavDesktopCategoryDropdownProps> = ({
  navCategory,
  buttonRef,
}) => {
  const setActiveMenu = useSetAtom(activeNavDropdownAtom);
  const columns = navCategory.navColumns;
  const filledColumnCount = columns.length;
  const emptyColumnCount = TOTAL_NUMBER_OF_COLUMNS - filledColumnCount;

  const closeNavigation = () => setActiveMenu(-1);

  return (
    <NavMenuDrawer navCategoryTitle={navCategory.title} buttonRef={buttonRef}>
      <NavShopAllLink
        to={`${navCategory.pageSlug}`}
        onClick={closeNavigation}
        text={navCategory.shopAllCTAText}
        ariaLabel={navCategory.shopAllCTAText}
        sx={{ pt: "s500", width: "fit-content" }}
      />
      <Grid
        sx={{
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "s700",
          pt: "s700",
          pb: "s700",
          width: "100%",
          alignItems: "flex-start",
          alignSelf: "stretch",
        }}
        variant="wide"
      >
        {columns.map(
          (column, columnIndex) =>
            column.items.length > 0 && (
              <Box key={columnIndex}>
                {column.items.map((subCategory) => (
                  <Box
                    as="section"
                    aria-label={subCategory?.title}
                    key={subCategory.id}
                    sx={{ mb: "s700" }}
                  >
                    <Paragraph
                      variant="eyebrow01"
                      sx={{
                        color: "goldDark",
                        textTransform: "uppercase",
                        fontSize: ["fs10", null, "fs10"],
                        mb: "s200",
                      }}
                    >
                      {subCategory?.title}
                    </Paragraph>

                    <NavProductList
                      navCategory={navCategory}
                      subCategory={subCategory}
                      closeNavigation={closeNavigation}
                    />
                  </Box>
                ))}
              </Box>
            ),
        )}
        {navCategory.featuredImageModule && (
          <FeaturedImageModule
            {...navCategory.featuredImageModule}
            emptyColumnCount={emptyColumnCount}
            closeNavigation={closeNavigation}
          />
        )}
      </Grid>
    </NavMenuDrawer>
  );
};
